import { ProctorTypeLabel } from '@exam-center/proctor-management';
import {
  CancelOutlinedIcon,
  PendingOutlinedIcon,
  CheckCircleOutlineRoundedIcon,
  ChipGroup,
  EllipsisText,
  Flex,
  Text,
  type GridColDef,
} from '@shared/components';
import { formatDateTime } from '@shared/utils';

import { AdminProctorRequestStatusLabel } from '../admin-proctor-request-status-label';

import type {
  InReviewProctorRequest,
  RejectedProctorRequest,
  SupervisorInReviewProctorRequest,
} from './types';

function renderVerdictResult(verdict: 'APPROVED' | 'REJECTED' | 'PENDING' | null) {
  let content;
  switch (verdict) {
    case 'APPROVED':
      content = (
        <>
          <CheckCircleOutlineRoundedIcon sx={{ color: (theme) => theme.palette.success.main }} />
          <Text>อนุมัติแล้ว</Text>
        </>
      );
      break;
    case 'REJECTED':
      content = (
        <>
          <CancelOutlinedIcon sx={{ color: (theme) => theme.palette.error.main }} />
          <Text>ไม่อนุมัติ</Text>
        </>
      );
      break;
    case 'PENDING':
      content = (
        <>
          <PendingOutlinedIcon sx={{ color: (theme) => theme.palette.warning.main }} />
          <Text>รออนุมัติ</Text>
        </>
      );
      break;
    default:
      content = <Text>-</Text>;
  }

  return <Flex sx={{ gap: (theme) => theme.spacing(2) }}>{content}</Flex>;
}

export const inReviewColumns: GridColDef<InReviewProctorRequest>[] = [
  {
    field: 'refCode',
    headerName: 'เลขที่คำขอ',
    width: 97,
    sortable: false,
    renderCell: ({ row }) => {
      if (row.refCode) {
        return <Text>{row.refCode}</Text>;
      }
      return <Text sx={{ textAlign: 'center', width: '100%' }}>-</Text>;
    },
  },
  {
    field: 'name',
    headerName: 'ชื่อผู้คุมสอบ',
    flex: 1,
    sortable: false,
    minWidth: 200,
    renderCell: ({ row }) => {
      return <EllipsisText>{row.name}</EllipsisText>;
    },
  },
  {
    field: 'type',
    headerName: 'ประเภทผู้คุมสอบ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <ProctorTypeLabel variant={row.type} />;
    },
  },
  {
    field: 'examCenter',
    headerName: 'ศูนย์ทดสอบ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      const { examCenter } = row;

      if (examCenter && examCenter.length === 0) {
        return <Text>-</Text>;
      } else {
        return <ChipGroup labels={[examCenter ?? '-']} limit={3} direction='row' />;
      }
    },
  },
  {
    field: 'status',
    headerName: 'สถานะ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <AdminProctorRequestStatusLabel variant={row.status} />;
    },
  },
  {
    field: 'requestedAt',
    headerName: 'วันที่ส่งคำขอ',
    width: 160,
    renderCell: ({ row }) => {
      return <Text>{formatDateTime(row.requestedAt)}</Text>;
    },
  },
];

export const supervisorInReviewColumns: GridColDef<SupervisorInReviewProctorRequest>[] = [
  {
    field: 'refCode',
    headerName: 'เลขที่คำขอ',
    width: 97,
    sortable: false,
    renderCell: ({ row }) => {
      if (row.refCode) {
        return <Text>{row.refCode}</Text>;
      }
      return <Text sx={{ textAlign: 'center', width: '100%' }}>-</Text>;
    },
  },
  {
    field: 'name',
    headerName: 'ชื่อผู้คุมสอบ',
    flex: 1,
    sortable: false,
    minWidth: 200,
    renderCell: ({ row }) => {
      return <EllipsisText>{row.name}</EllipsisText>;
    },
  },
  {
    field: 'type',
    headerName: 'ประเภทผู้คุมสอบ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <ProctorTypeLabel variant={row.type} />;
    },
  },
  {
    field: 'examCenter',
    headerName: 'ศูนย์ทดสอบ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      const { examCenter } = row;

      if (examCenter && examCenter.length === 0) {
        return <Text>-</Text>;
      } else {
        return <ChipGroup labels={[examCenter ?? '-']} limit={3} direction='row' />;
      }
    },
  },
  {
    field: 'status',
    headerName: 'สถานะ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <AdminProctorRequestStatusLabel variant={row.status} />;
    },
  },
  {
    field: 'firstReviewVerdict',
    headerName: 'รอบการอนุมัติที่ 1',
    width: 160,
    sortable: false,

    renderCell: ({ row }) => {
      return renderVerdictResult(row.firstReviewVerdict);
    },
  },
  {
    field: 'secondReviewVerdict',
    headerName: 'รอบการอนุมัติที่ 2',
    width: 160,
    sortable: false,

    renderCell: ({ row }) => {
      return renderVerdictResult(row.secondReviewVerdict);
    },
  },
  {
    field: 'requestedAt',
    headerName: 'วันที่ส่งคำขอ',
    width: 160,
    renderCell: ({ row }) => {
      return <Text>{formatDateTime(row.requestedAt)}</Text>;
    },
  },
];

export const rejectedColumns: GridColDef<RejectedProctorRequest>[] = [
  {
    field: 'refCode',
    headerName: 'เลขที่คำขอ',
    width: 97,
    sortable: false,
    renderCell: ({ row }) => {
      if (row.refCode) {
        return <Text>{row.refCode}</Text>;
      }
      return <Text sx={{ textAlign: 'center', width: '100%' }}>-</Text>;
    },
  },
  {
    field: 'name',
    headerName: 'ชื่อผู้คุมสอบ',
    flex: 1,
    sortable: false,
    minWidth: 200,
    renderCell: ({ row }) => {
      return <EllipsisText>{row.name}</EllipsisText>;
    },
  },
  {
    field: 'type',
    headerName: 'ประเภทผู้คุมสอบ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <ProctorTypeLabel variant={row.type} />;
    },
  },
  {
    field: 'examCenter',
    headerName: 'ศูนย์ทดสอบ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      const { examCenter } = row;

      if (examCenter && examCenter.length === 0) {
        return <Text>-</Text>;
      } else {
        return <ChipGroup labels={[examCenter ?? '-']} limit={3} direction='row' />;
      }
    },
  },
  {
    field: 'status',
    headerName: 'สถานะ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <AdminProctorRequestStatusLabel variant={row.status} />;
    },
  },
  {
    field: 'firstReviewVerdict',
    headerName: 'รอบการอนุมัติที่ 1',
    width: 160,
    sortable: false,

    renderCell: ({ row }) => {
      return renderVerdictResult(row.firstReviewVerdict);
    },
  },
  {
    field: 'secondReviewVerdict',
    headerName: 'รอบการอนุมัติที่ 2',
    width: 160,
    sortable: false,

    renderCell: ({ row }) => {
      return renderVerdictResult(row.secondReviewVerdict);
    },
  },
  {
    field: 'rejectedAt',
    headerName: 'วันที่ไม่อนุมัติ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <Text>{formatDateTime(row.rejectedAt)}</Text>;
    },
  },
];
